import { takeEvery, call, put, delay } from 'redux-saga/effects';
import callApi from '../../utils/callApi';
import callApiFormData from '../../utils/callApiFormData';
import { FundingActionTypes, fetchFundings, fetchFundingByReference, fetchExports, setSearchRequestFailed, setSearchRequestReferenceFailed, setExportRequestError, deleteSelectedFundingsResponse, uploadFundingDataSuccess, uploadFundingDataFailed, successGetFramewokrProgramme, fetchAdminEmailsSuccess } from './actions';

function* handleSearchRequestFundingReference(action: any): any {
  try {
    const {reference} = action.payload;
    const res = yield call(callApi, 'get', process.env.REACT_APP_SEARCH_API_URL || '', `/v1/fundings/${reference}`);
    yield put (fetchFundingByReference(res));
  } catch (err) {
    console.error(err);
    yield put(setSearchRequestReferenceFailed(true));
  }
}

function* handleSearchRequestFundings(action: any): any {
  try {
    const { frameworkProgramme, status, limit, offset } = action.payload;
    const pathParams = new URLSearchParams();
    pathParams.append('frameworkProgramme[]', frameworkProgramme);
    pathParams.append('status[]', status);
    pathParams.append('limit', limit.toString());
    pathParams.append('offset', offset.toString());
    
    const res = yield call(callApi, 'get', process.env.REACT_APP_SEARCH_API_URL || '', `/v1/fundings?${pathParams.toString()}`);
    yield put(fetchFundings(res.data, res.totalPages, res.totalRecords, res.currentPage, res.pageSize, frameworkProgramme, status));
  } catch (err) {
    console.error(err);
    yield put(setSearchRequestFailed(true));
  }
}

function* handleGetFrameworkProgramme(action: any): any {
  try {
    const res = yield call(callApi, 'get', process.env.REACT_APP_SEARCH_API_URL || '', '/v1/framework_programme');
    yield put(successGetFramewokrProgramme(res.data));
  } catch (err) {
    console.error(err);
  }
}

function* handleFetchAdminEmails(action: any): any {
  try {
    const res = yield call(callApi, 'get', process.env.REACT_APP_SEARCH_API_URL || '', '/v1/emails');
    yield put(fetchAdminEmailsSuccess(res));
  } catch (err) {
    console.error(err);
  }
}

function* handleExportRequest(action: any): any {
  try {
    const {frameworkProgramme, status} = action.payload;
    const pathParams = new URLSearchParams();
    pathParams.append('frameworkProgramme[]', frameworkProgramme);
    pathParams.append('status[]', status);

    const res = yield call(callApi, 'get', process.env.REACT_APP_SEARCH_API_URL || '', `/v1/fundings/exportall?${pathParams.toString()}`);
    yield put(fetchExports(res));
    
  } catch (err) {
    console.error(err);
    yield put(setExportRequestError(true, false));
  }
}

function* handleDeleteSelectedFundings(action: any): any {
  try {
    const {references} = action.payload;
    const pathParams = new URLSearchParams();
    pathParams.append('references[]', references);
    const res = yield call(callApi, 'delete', process.env.REACT_APP_SEARCH_API_URL || '', `/v1/fundings?${pathParams.toString()}`);
    yield put(deleteSelectedFundingsResponse(res));
  } catch (err) {
    console.error(err);
    yield put(deleteSelectedFundingsResponse(err));
  }
}

function* pollJobStatus(jobId: string): any {
  const maxAttempts = 150; // 5 minutes with 2-second intervals
  let attempts = 0;
  
  while (attempts < maxAttempts) {
    try {
      const jobStatus = yield call(callApi, 'get', process.env.REACT_APP_SEARCH_API_URL || '', `/v1/fundings/job/${jobId}`);
      
      if (jobStatus.status === 'completed') {
        return { success: true, message: jobStatus.result?.message || 'Upload completed successfully' };
      } else if (jobStatus.status === 'failed') {
        return { success: false, message: jobStatus.error || 'Upload failed' };
      }
      
      attempts++;
      yield delay(2000);
    } catch (err) {
      console.error('Error polling job status:', err);
      return { success: false, message: 'Error checking upload status' };
    }
  }
  
  // Timeout reached
  return { success: false, message: 'Upload timeout - please check the status later or try again' };
}

function* handleUploadFundingData(action: any): any {
  try {
    const { formData } = action.payload;
    const res = yield call(callApiFormData, 'post', process.env.REACT_APP_SEARCH_API_URL || '', '/v1/fundings/upload', formData);
    
    if (res.jobId) {
      // Poll for job completion
      const result = yield call(pollJobStatus, res.jobId);
      
      if (result.success) {
        yield put(uploadFundingDataSuccess({ statusCode: 201, message: result.message }));
      } else {
        yield put(uploadFundingDataFailed({ error: result.message }));
      }
    } else {
      // Fallback for old response format
      yield put(uploadFundingDataSuccess(res));
    }
  } catch (err) {
    console.error(err);
    yield put(uploadFundingDataFailed(err));
  }
}

export default function* fundingSaga() {
  yield takeEvery(FundingActionTypes.EXPORT_REQUEST, handleExportRequest);
  yield takeEvery(FundingActionTypes.SEARCH_REQUEST, handleSearchRequestFundings);
  yield takeEvery(FundingActionTypes.SEARCH_REQUEST_REFERENCE, handleSearchRequestFundingReference);
  yield takeEvery(FundingActionTypes.DELETE_SELECTED_FUNDINGS, handleDeleteSelectedFundings);
  yield takeEvery(FundingActionTypes.UPLOAD_FUNDING_DATA, handleUploadFundingData);
  yield takeEvery(FundingActionTypes.GET_FRAMEWORK_PROGRAMME, handleGetFrameworkProgramme);
  yield takeEvery(FundingActionTypes.FETCH_ADMIN_EMAILS, handleFetchAdminEmails);
}